import { defineStore } from 'pinia'

import { useNotificationStore } from './NotificationStore.js';
import {
  signIn as AuthSignIn,
  signOut as AuthSignOut,
  confirmSignUp as AuthConfirmSignUp,
  confirmSignIn as AuthConfirmSignIn,
  setUpTOTP as AuthSetUpTOTP,
  verifyTOTPSetup as AuthVerifyTOTPSetup,
  fetchMFAPreference as AuthFetchMFAPreference,
  getCurrentUser as AuthGetCurrentUser,
  updateMFAPreference as AuthUpdateMFAPreference,
  fetchAuthSession as AuthFetchAuthSession,
  resetPassword as AuthResetPassword,
  confirmResetPassword as AuthConfirmResetPassword,
  updatePassword as AuthUpdatePassword,
  signInWithRedirect as AuthSignInWithRedirect,
  // fetchUserAttributes as AuthFetchUserAttributes,
} from 'aws-amplify/auth'
import Api from '../api/Api'
import Helper from '@/components/helper';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    dbUser: null,

    tenants: [],
    activeTenantId: window.localStorage.activetenantid || null,
    rootNodeId: window.localStorage.contextnodeid || null,

    forceLogout: false,
    sessionInvalidatedLogoutPending: false,
    sessionInvalidatedDialogVisible: false,
  }),
  getters: {
    // user: function (state) {
    //   return state.user;
    // },
    // dbUser: function (state) {
    //   return state.dbUser;
    // },
    getTenants: function(state) {
      return state.tenants;
    },
    getActiveTenant: function(state) {
      return state.tenants.find(x => x.id === state.activeTenantId);
    },
    getActiveTenantId: function(state) {
      return state.activeTenantId;
    },
    getAccessPoints: function() {
      return this.getAccesspoints;
    },
    getAccesspoints: function(state) {
      let accesspoints = [];

      for (const tenantItem of state.tenants) {
        for (const accesspoint of tenantItem.accessPoints) {
          accesspoints.push(accesspoint);
        }
      }

      return accesspoints;
    },
    getAccessPointIds: function() {
      let accesspointIDs = [];
      for (const accesspoint of this.getAccesspoints) {
        accesspointIDs.push(accesspoint.node.id);
      }
      return accesspointIDs;
    },
    getActiveAccesspoint: function(state) {
      return this.getAccesspoints.find(x => x.node.id === state.rootNodeId);
    },

    tenantIsExpired: function() {
      if (this?.getActiveTenant?.subscription?.subscriptionEnd) {
        if (this.getActiveTenant.subscription.subscriptionEnd.minutes > 0) {
          return false;
        }

        return true;
      }

      return false;
    },
    subscriptionExpireTimestamp: function() {
      if (this.getActiveTenant &&
          this.getActiveTenant.subscription &&
          this.getActiveTenant.subscription.subscriptionEnd) {
        return parseInt(this.getActiveTenant.subscription.subscriptionEnd.seconds * 1000);
      }
      return false;
    },

    // forceLogout: function(state) {
    //   return state.forceLogout;
    // },
    // rootNodeId: function(state) {
    //   return state.rootNodeId;
    // },
  },
  actions: {
    _updateUser(user) {
      if (user && user.sub) {
        user.uuid = user.sub;
      }
      this.user = user;
    },
    _setActiveTenantId: function(tenantId) {
      window.localStorage.activetenantid = tenantId;
      this.activeTenantId = tenantId;
    },
    _setRootNodeId: function(rootNodeId) {
      window.localStorage.contextnodeid = rootNodeId;
      this.rootNodeId = rootNodeId;
    },
    updateDBUserProperty: function(payload) {
      if (this.dbUser && this.dbUser[payload.property]) {
        this.dbUser[payload.property] = payload.value;
      }
    },
    updateUserSettingValue: function(payload) {
      if (this.dbUser && this.dbUser.settings && (payload.name in this.dbUser.settings)) {
        this.dbUser.settings[payload.name] = payload.value.toString();
      }
    },


    async signIn(loginParams) {
      try {
        // just in case that there is a user logged in
        await AuthSignOut();
      } catch (error) {
        console.log("Try to logout before login", error);
      }

      const signInResponse = await AuthSignIn(loginParams);
      // context.dispatch('afterSignIn', signInResponse);
      this.afterSignIn(signInResponse);
      return signInResponse;
    },
    async signInWithProvider(options) {
      try {
        // just in case that there is a user logged in
        await AuthSignOut();
      } catch (error) {
        console.log("Try to logout before login", error);
      }

      await AuthSignInWithRedirect(options);
    },

    async signOut() {
      await AuthSignOut();

      // unsubscribe from notifications
      const notificationStore = useNotificationStore();
      notificationStore.unsubscribe();

      this._updateUser(null);
      this.sessionInvalidatedLogoutPending = false;
      this.sessionInvalidatedDialogVisible = false;
      if (Helper.isFCPMessageHandler('signOutUser')) {
          const signOutObject = JSON.stringify({ action: "signOutUser" });
          Helper.invokeFCPMessageHandler('signOutUser', signOutObject);
      }
    },

    confirmSignUp: function(params) {
      return AuthConfirmSignUp(params.email, params.code);
    },
    setupTOTP: function() {
      return AuthSetUpTOTP();
    },
    verifyTOTPToken: function(payload) {
      return AuthVerifyTOTPSetup(payload);
    },
    enableTOTPMFA: function() {
      return AuthUpdateMFAPreference({ totp: 'ENABLED' });
    },
    disableTOTPMFA: function() {
      return AuthUpdateMFAPreference({ totp: 'DISABLED' });
    },
    fetchMFAPreference() {
      return AuthFetchMFAPreference()
    },
    confirmTOTP: async function(code) {
      const signInResponse = await AuthConfirmSignIn({
        challengeResponse: code,
      });
      this.afterSignIn(signInResponse);
      return signInResponse;
    },

    async afterSignIn(signInResponse) {
      if (signInResponse.isSignedIn) {
        this.refreshCognitoUser();
      }
    },

    async refreshCognitoUser() {
      const user = await AuthGetCurrentUser();

      if (!user || !user.userId) {
        throw "User not authenticated!";
      }

      if (Helper.isFCPMessageHandler('setUser')) {
        const userObject = JSON.stringify({action: "setUser", userID: user.userId});
        Helper.invokeFCPMessageHandler('setUser', userObject);
      }

      // console.log("Current user is", user);

      this._updateUser(user);

      try {
        const session = await AuthFetchAuthSession();
        // console.log("Current session is", session);

        if (session && session.tokens) {
          if (Helper.isFCPMessageHandler('refreshUser')) {
            let idToken = session.tokens.idToken.toString();
            let accessToken = session.tokens.accessToken.toString();
            const sessionObject = JSON.stringify({action: "refreshUser", idToken: idToken, accessToken: accessToken});
            Helper.invokeFCPMessageHandler('refreshUser', sessionObject);
          }
        }
      } catch (error) {
        if (window.webkit) {
          delete window.webkit;
        }

        console.error(error);
      }

      return user;
    },
    async refreshDBUser() {
      try {
        const response = await Api.user.getMe();
        // The user's abilities are stored in the tenant's access point group.
        // It does not come with the user object anymore.
        const user = response.data.user;
        // user.abilities = firstTenantData.accessPoint.group.abilities;
        // user.abilities = response.data.tenants[0].accessPoints[0].group.abilities;
        // user.tenants = response.data.tenants;
        // store the abilities in the local storage
        // they are loaded up in App.vue
        // and then refreshed in MainLayout and ConsoleLayout
        // window.localStorage.abilities = JSON.stringify(response.data.user.abilities);
        window.localStorage.settings = JSON.stringify(response.data.user.settings);
        window.localStorage.languageInterface = response.data.user.settings.language;

        this.dbUser = response.data.user;
        this.tenants = response.data.tenants;
        return user;
      } catch (e) {
        console.log("Error while refreshing DB user information:", e?.response?.data?.error || e);
      }
    },
    saveSettings: async function(payload) {
      const userID = this.dbUser.id;

      await Api.user.saveSettings(userID, payload);
      return this.refreshDBUser();
    },
    getCurrentSession: function() {
      return AuthFetchAuthSession();
    },
    forgotPassword: function(email) {
      return AuthResetPassword({username: email});
    },
    forgotPasswordSubmit: function(params) {
      return AuthConfirmResetPassword({
        username: params.email,
        newPassword: params.password,
        confirmationCode: params.code,
      });
    },
    changePassword: function(params) {
      return AuthUpdatePassword({
        oldPassword: params.oldPassword,
        newPassword: params.newPassword,
      });
    },
    completePassword: async function(params) {
      const signInResponse = await AuthConfirmSignIn({
        challengeResponse: params.password,
        options: params.options,
      });
      this.afterSignIn(signInResponse);
      return signInResponse;
    },
    setRootNodeId: function(nodeId) {
      if (!this.getAccessPointIds || this.getAccessPointIds.indexOf(nodeId) < 0) {
        return;
      }

      for (let i = 0; i < this.getTenants.length; i++) {
        for (let o = 0; o < this.getTenants[i].accessPoints.length; o++) {
          if (this.getTenants[i].accessPoints[o].node.id === nodeId) {
            this._setActiveTenantId(this.getTenants[i].id)
            this._setRootNodeId(this.getTenants[i].accessPoints[o].node.id);
            return;
          }
        }
      }
    },
    createFreeTrial: function(payload) {
      return Api.common.createFreeTrial(payload);
    },
    salesmanCreatesTenant: function(payload) {
      return Api.common.salesmanCreatesTenant(payload);
    },
    disableUser: function() {
      this._setActiveTenantId(null);
      this._setRootNodeId(null);
      this.forceLogout = true;
    },
    showSessionInvalidatedLogout: async function() {
      if (this.sessionInvalidatedDialogVisible) {
        return;
      }

      this.sessionInvalidatedLogoutPending = true;
      this.sessionInvalidatedDialogVisible = true;
      await AuthSignOut();

      // unsubscribe from notifications
      const notificationStore = useNotificationStore();
      notificationStore.unsubscribe();

      if (Helper.isFCPMessageHandler('signOutUser')) {
        const signOutObject = JSON.stringify({ action: "signOutUser" });
        Helper.invokeFCPMessageHandler('signOutUser', signOutObject);
      }
    },
    confirmSessionInvalidatedLogout: function(router) {
      if (!this.sessionInvalidatedDialogVisible) {
        return;
      }

      this.sessionInvalidatedDialogVisible = false;

      if (router?.currentRoute?.value?.path?.toLowerCase().startsWith('/account/logout')) {
        return;
      }

      router.push('/account/logout');
    },
  },
})
